import {
  CASE_STAGES,
  stageForStatus,
  CLOSED_STAGE_INDEX,
} from '../components/CaseStatusTimeline/CaseStatusTimeline';
import { TicketDto } from '../views/HomeView/HomeView';

/**
 * Lloyd's Jul 8 feedback: the dashboard showed ~50 cases labelled "approving" — legacy
 * (Jimmy-era) wording that doesn't track the 8 stages. Almost all of those are imported Clio
 * cases that never got a TicketStatus row even though their LOR was already sent.
 *
 * This helper derives the correct 8-stage position for a case even when it has no status row,
 * falling back to the LOR flags the importer *did* set. It gives the dashboard a truthful stage
 * label and lets "Cases Needing Action" stop counting already-represented imported cases.
 */

// 0-indexed stages (see CASE_STAGES): 0 Pending payment · 1 LOR sent · 2 LOR confirmed · …
const STAGE_PENDING = 0;
const STAGE_LOR_SENT = 1;
const STAGE_LOR_CONFIRMED = 2;

/**
 * Best-effort stage index for a case. Prefers the real status; when absent, infers from the
 * LOR lifecycle flags (confirmed → sent → pending). Returns -1 only when nothing is known.
 */
export function deriveStageIndex(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
  statusName: string | null | undefined,
): number {
  const fromStatus = stageForStatus(statusName);
  if (fromStatus >= 0) return fromStatus;

  if (ticket.Ticket_LorConfirmedAt) return STAGE_LOR_CONFIRMED;
  if (ticket.Ticket_LorSent) return STAGE_LOR_SENT;
  return STAGE_PENDING;
}

/** Human label ("2. LOR sent") for the derived stage, for chips/summaries. */
export function deriveStageLabel(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
  statusName: string | null | undefined,
): string {
  const i = deriveStageIndex(ticket, statusName);
  const stage = CASE_STAGES[i];
  return stage ? `${i + 1}. ${stage.short}` : 'Unknown';
}

export function isClosedStage(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
  statusName: string | null | undefined,
): boolean {
  return deriveStageIndex(ticket, statusName) === CLOSED_STAGE_INDEX;
}

/**
 * A case "needs action" when staff must do the next thing. For cases with an explicit status we
 * use the ACTION_STATUSES set (owned by the dashboard). For cases with NO status we only flag the
 * genuinely-new ones — hired but the LOR has not gone out yet. Imported cases whose LOR is already
 * sent are NOT pending; this is what collapses Lloyd's inflated ~50 down to the true handful.
 */
export function noStatusNeedsAction(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
): boolean {
  return !ticket.Ticket_LorSent && !ticket.Ticket_LorConfirmedAt;
}
